/**
 * DynamicChart - 동적 차트 렌더링 컴포넌트
 * 백엔드 API의 chart_config를 받아서 자동으로 Line/Bar 차트를 렌더링
 */
import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  ScatterChart,
  Scatter,
  Cell,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useTranslation } from '../context/LanguageContext';

const DynamicChart = ({ config }) => {
  const { t } = useTranslation();
  if (!config) return null;

  const {
    type,
    data,
    xAxisKey,
    yAxisKeys,
    title,
    options = {}
  } = config;

  const {
    height = 400,
    showGrid = true,
    showLegend = true,
    showTooltip = true
  } = options;

  // 차트 색상 팔레트 - 파스텔톤
  const colors = [
    '#A8D5FF', // 연한 파랑 (Pastel Blue)
    '#B4E7CE', // 연한 민트 (Pastel Mint)
    '#FFD4A3', // 연한 복숭아 (Pastel Peach)
    '#FFB4B4', // 연한 핑크 (Pastel Pink)
    '#D4C5F9', // 연한 보라 (Pastel Purple)
    '#B4F0F0'  // 연한 시안 (Pastel Cyan)
  ];

  // Line 차트 렌더링
  if (type === 'line') {
    return (
      <div className="chart-section">
        {title && <h4>{title}</h4>}
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" opacity={0.5} />}
            <XAxis
              dataKey={xAxisKey}
              stroke="#9CA3AF"
            />
            <YAxis stroke="#9CA3AF" />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yAxisKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar 차트 렌더링
  if (type === 'bar') {
    return (
      <div className="chart-section">
        {title && <h4>{title}</h4>}
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" opacity={0.5} />}
            <XAxis
              dataKey={xAxisKey}
              stroke="#9CA3AF"
            />
            <YAxis stroke="#9CA3AF" />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yAxisKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                radius={[8, 8, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Area 차트 렌더링
  if (type === 'area') {
    return (
      <div className="chart-section">
        {title && <h4>{title}</h4>}
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" opacity={0.5} />}
            <XAxis
              dataKey={xAxisKey}
              stroke="#9CA3AF"
            />
            <YAxis stroke="#9CA3AF" />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yAxisKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.6}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Pie 차트 렌더링
  if (type === 'pie') {
    // Pie 차트는 단일 시리즈만 지원
    const valueKey = yAxisKeys[0];
    const nameKey = xAxisKey;

    return (
      <div className="chart-section">
        {title && <h4>{title}</h4>}
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey={valueKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              outerRadius={120}
              label={(entry) => `${entry[nameKey]}: ${entry[valueKey]}`}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Scatter 차트 렌더링
  if (type === 'scatter') {
    return (
      <div className="chart-section">
        {title && <h4>{title}</h4>}
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" opacity={0.5} />}
            <XAxis
              type="number"
              dataKey={xAxisKey}
              name={xAxisKey}
              stroke="#9CA3AF"
            />
            <YAxis
              type="number"
              dataKey={yAxisKeys[0]}
              name={yAxisKeys[0]}
              stroke="#9CA3AF"
            />
            {yAxisKeys.length > 1 && <ZAxis type="number" dataKey={yAxisKeys[1]} range={[60, 400]} />}
            {showTooltip && <Tooltip cursor={{ strokeDasharray: '3 3' }} />}
            {showLegend && <Legend />}
            <Scatter
              name={yAxisKeys[0]}
              data={data}
              fill={colors[0]}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="chart-section">
      <p>{t('components.dynamicChart.unsupported', { type })}</p>
    </div>
  );
};

export default DynamicChart;
